import axios from 'axios';
import { api } from './client';
import { getMainApiBaseUrl } from '@/config/apiEnv';

export interface AppPublicLinkAnalytics {
  views: number;
  interactions: number;
  referrers: Record<string, number>;
}

export interface AppPublicLink {
  id: string;
  token: string;
  app_id: string;
  url: string;
  allow_interaction: boolean;
  has_password: boolean;
  status: 'active' | 'revoked' | 'expired';
  created_at: string;
  expires_at?: string;
  revoked_at?: string;
  analytics: AppPublicLinkAnalytics;
}

export interface CreatePublicLinkRequest {
  expires_in_days?: number;
  password?: string;
  allow_interaction?: boolean;
}

export interface PublicSharedAppResponse {
  app: Record<string, unknown>;
  allow_interaction: boolean;
  password_required?: boolean;
  access_grant?: string;
  link?: Partial<AppPublicLink>;
}

const publicApi = axios.create({
  baseURL: getMainApiBaseUrl(),
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

function extractError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string; message?: string } | undefined;
    return data?.error || data?.message || error.message;
  }
  return error instanceof Error ? error.message : 'Request failed';
}

export const appPublicLinkService = {
  async create(appId: string, body: CreatePublicLinkRequest): Promise<AppPublicLink> {
    const response = await api.post<{ link: AppPublicLink }>(`/apps/${appId}/public-links`, body);
    return response.data.link;
  },

  async list(appId: string): Promise<AppPublicLink[]> {
    const response = await api.get<{ links: AppPublicLink[] }>(`/apps/${appId}/public-links`);
    return response.data.links ?? [];
  },

  async revoke(appId: string, linkId: string): Promise<void> {
    await api.delete(`/apps/${appId}/public-links/${linkId}`);
  },

  async fetchPublicApp(token: string, grant?: string): Promise<PublicSharedAppResponse> {
    try {
      const response = await publicApi.get<PublicSharedAppResponse>(`/public/apps/${token}`, {
        headers: grant ? { 'X-Public-Link-Grant': grant } : undefined,
        validateStatus: (status) => status === 200 || status === 401,
      });
      return response.data;
    } catch (error) {
      throw new Error(extractError(error));
    }
  },

  async unlockPublicApp(token: string, password: string): Promise<PublicSharedAppResponse> {
    try {
      const response = await publicApi.post<PublicSharedAppResponse>(`/public/apps/${token}/unlock`, {
        password,
      });
      return response.data;
    } catch (error) {
      throw new Error(extractError(error));
    }
  },

  async recordInteraction(token: string, grant?: string): Promise<void> {
    await publicApi.post(
      `/public/apps/${token}/interact`,
      {},
      { headers: grant ? { 'X-Public-Link-Grant': grant } : undefined }
    );
  },

  grantStorageKey(token: string): string {
    return `khiimori_public_link_grant:${token}`;
  },

  getStoredGrant(token: string): string | null {
    try {
      return sessionStorage.getItem(this.grantStorageKey(token));
    } catch {
      return null;
    }
  },

  storeGrant(token: string, grant: string): void {
    try {
      sessionStorage.setItem(this.grantStorageKey(token), grant);
    } catch {
      // ignore quota errors
    }
  },
};
